import { useCallback, useEffect, useRef, useState } from 'react'
import { ThemeContext, type ResolvedTheme, type Theme } from './theme-context'

function readStoredTheme(): Theme {
  const stored = localStorage.getItem('cc-theme')
  if (stored === 'dark' || stored === 'light') return stored
  if (stored === 'system') {
    const migrated: Theme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    localStorage.setItem('cc-theme', migrated)
    return migrated
  }
  return 'dark'
}

type ThemeProviderProps = {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme)
  const forcedStackRef = useRef<ResolvedTheme[]>([])
  const [forcedTop, setForcedTop] = useState<ResolvedTheme | null>(null)

  useEffect(() => {
    localStorage.setItem('cc-theme', theme)
  }, [theme])

  useEffect(() => {
    document.documentElement.dataset.theme = forcedTop ?? theme
  }, [forcedTop, theme])

  function setTheme(next: Theme) {
    setThemeState(next)
  }

  const pushForcedTheme = useCallback((forced: ResolvedTheme) => {
    forcedStackRef.current = [...forcedStackRef.current, forced]
    setForcedTop(forcedStackRef.current[forcedStackRef.current.length - 1])
    return () => {
      const idx = forcedStackRef.current.lastIndexOf(forced)
      if (idx >= 0) {
        forcedStackRef.current = [
          ...forcedStackRef.current.slice(0, idx),
          ...forcedStackRef.current.slice(idx + 1),
        ]
      }
      setForcedTop(forcedStackRef.current[forcedStackRef.current.length - 1] ?? null)
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme: theme, setTheme, pushForcedTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
